import React, { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useHistory } from 'react-router-dom'
import cn from 'classnames'

import styles from './styles.module.scss'

import { getDocumentTypesGrouped } from '../../../api'

import { PlusIcon } from '../Icons'
import SearchIcon from '../../Icons/SearchIcon'
import Input from '../../Core/Input/Input'

const getBadges = (documentType, t) => {
  const badges = []

  badges.push({ label: documentType.ownership === 'company' ? t('Company document') : t('Employee document') })

  if (documentType.expiration_date) {
    badges.push({ label: t('Expiration required') })
  }

  if (documentType.employee_upload) {
    badges.push({ label: t('Employee upload allowed'), highlight: true })
  }

  if (documentType.reminder_days) {
    badges.push({ label: t('Reminder enabled') })
  }

  if (documentType.require_acknowledgment) {
    badges.push({ label: t('Acknowledgement'), warning: true })
  }

  if (documentType.require_approval) {
    badges.push({ label: t('Approval'), warning: true })
  }

  if (documentType.require_signature) {
    badges.push({ label: t('E-signature'), warning: true })
  }

  if (documentType.track_reading_time) {
    badges.push({ label: t('Reading tracking'), warning: true })
  }

  return badges
}

const DocumentTypes = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const { id: companyId } = useParams()
  const [documentTypesGrouped, setDocumentTypesGrouped] = useState([])
  const [search, setSearch] = useState('')

  useEffect(() => {
    if (companyId) {
      init(companyId)
    }
    
  }, [companyId])

  const init = async (companyId) => {
    const res = await getDocumentTypesGrouped(companyId)
    if (res?.success) {
      setDocumentTypesGrouped(res.data)
    }
  }

  const handleCreateDocumentType = () => {
    history.push({search: `?tab=document_type`})
  }

  const handleEditDocumentType = (documentType) => {
    history.push({search: `?tab=document_type&id=${documentType.id}`})
  }

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase()
    if (!query) {
      return documentTypesGrouped
    }

    return documentTypesGrouped
      .map(group => ({
        ...group,
        document_types: group.document_types.filter(
          documentType => documentType.name.toLowerCase().includes(query)
        ),
      }))
  }, [documentTypesGrouped, search])

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.searchWrapper}>
          <Input
            icon={<SearchIcon />}
            placeholder={`${t('Search')}...`}
            width='100%'
            height='36px'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {
          filteredGroups.filter(group => group.document_types.length).map(group => (
            <div key={group.id} className={styles.group}>
              <div className={styles.groupTitle}>{ group.name }</div>
              <div className={styles.groupList}>
                {
                  group.document_types.map(documentType => (
                    <div
                      key={documentType.id}
                      className={styles.row}
                      onClick={() => handleEditDocumentType(documentType)}
                    >
                      <div className={styles.rowInfo}>
                        <div className={styles.rowTitle}>{ documentType.name }</div>
                        <div className={styles.rowBadges}>
                          {
                            getBadges(documentType, t).map((badge, index) => (
                              <span
                                key={index}
                                className={cn(styles.badge, { [styles.badgeHighlight]: badge.highlight, [styles.badgeWarning]: badge.warning })}
                              >
                                { badge.label }
                              </span>
                            ))
                          }
                        </div>
                      </div>
                      <div className={styles.rowCount}>
                        { t('{{count}} documents', { count: documentType.documents_count || 0 }) }
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          ))
        }
      </div>
    </div>
  )
}

export default DocumentTypes

